import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Load something, and know which of the four states you are in.
 *
 * The old console had one of these written by hand on every screen, and each
 * one told a slightly different story when it failed — some blanked the table,
 * some left "Loading…" sitting there forever. A screen has four states and all
 * four are worth drawing: loading, error, empty, and the thing itself.
 */
export function useAsync<T>(fn: (signal: AbortSignal) => Promise<T>, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);
  /* A reload while one is already in flight would otherwise race, and the
     slower answer would win — showing a list the user has already changed. */
  const seq = useRef(0);

  const run = useCallback(async () => {
    const mine = ++seq.current;
    const ctrl = new AbortController();
    setLoading(true);
    try {
      const value = await fn(ctrl.signal);
      if (mine === seq.current) { setData(value); setError(null); }
    } catch (e) {
      if (mine === seq.current && (e as Error).name !== 'AbortError') setError(e as Error);
    } finally {
      if (mine === seq.current) setLoading(false);
    }
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => { void run(); }, [run]);

  return { data, error, loading, reload: run };
}
